import { ChangeDetectionStrategy, Component, computed, inject, Input, Signal } from '@angular/core';
import { take } from 'rxjs';
import { ActionModalComponent, MODAL_TOKEN, ModalProvider, TOAST_TOKEN, ToastProvider } from '@core';
import { SetModerationFlagRequestDtoFlag } from '@core-http';
import { UsersListService } from '@admin-panel/infra';
import { OrIconComponent } from '@ui-components';
import { AdminUserQueryResult, SetUserModerationFlagCommandHandler } from '../../../application/handlers';
import { ModerationCommentComponent } from '../moderation-comment/moderation-comment.component';

interface ModerationAction {
  readonly flag: SetModerationFlagRequestDtoFlag;
  readonly label: string;
  readonly icon: string;
  readonly value: boolean;
  readonly disabled: boolean;
}

@Component({
  selector: 'lib-moderate-user',
  templateUrl: './moderate-user.component.html',
  styleUrls: ['./moderate-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrIconComponent],
  providers: [SetUserModerationFlagCommandHandler, UsersListService]
})
export class ModerateUserComponent {
  @Input() user!: AdminUserQueryResult;

  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly setUserModerationFlagCommandHandler: SetUserModerationFlagCommandHandler = inject(
    SetUserModerationFlagCommandHandler
  );

  readonly actions: Signal<ModerationAction[]> = computed(() => [
    {
      flag: SetModerationFlagRequestDtoFlag.BANNED,
      label: this.user.isBanned ? 'Cofnij bana' : 'Zabanuj',
      icon: 'blocked',
      value: !this.user.isBanned,
      disabled: true
    },
    {
      flag: SetModerationFlagRequestDtoFlag.HIGHLIGHTED,
      label: this.user.isHighlighted ? 'Cofnij wyróżnik' : 'Przyznaj wyróżnik',
      icon: 'star',
      value: !this.user.isHighlighted,
      disabled: false
    },
    {
      flag: SetModerationFlagRequestDtoFlag.VERIFIED,
      label: this.user.isVerified ? 'Cofnij weryfikację' : 'Przyznaj weryfikację',
      icon: 'check',
      value: !this.user.isVerified,
      disabled: true
    }
  ]);

  select(action: ModerationAction): void {
    if (action.disabled) return;

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: action.label,
        message: `${this.user.name} ${this.user.lastName}`.trim(),
        btnOk: 'Potwierdź',
        btnCancel: 'Anuluj',
        template: ModerationCommentComponent,
        requireTemplateSelection: true,
        action: (comment?: unknown) => this.setFlag(action, comment as string)
      }
    });
  }

  private setFlag(action: ModerationAction, comment: string): void {
    this.setUserModerationFlagCommandHandler
      .setFlag({
        userId: this.user.userId,
        flag: action.flag,
        value: action.value,
        comment: comment
      })
      .pipe(take(1))
      .subscribe(() => {
        this.modalProvider.dismissAllStoredModals();
        this.toastProvider.showToast({ message: 'Zapisano zmianę' });
      });
  }
}
